import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { Flex, Tooltip } from "antd";
import { createStyles } from "antd-style";

import { useFetchSecurityGroups } from "@av/apps/common-apps/platform/services/admin-settings/security-groups";
import { App } from "@av/components/atoms/app/App";
import { Button } from "@av/components/atoms/Button";
import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { CheckableTag } from "@av/components/atoms/tag/Tag";
import { Typography } from "@av/components/atoms/typography/Typography";
import {
  useCreatePlatformUser,
  useFetchAtsAdminSettingsUsers,
  useFetchPermissions,
  useSendWelcomeEmail,
  useUpdateUserPermission,
} from "../../../../services/admin-settings/admin-settings";
import {
  type AtsAdminSettingsUser,
  type SecurityGroup,
} from "../../../../services/admin-settings/type";
import { filterAtsAdminSettingsUsers } from "../../../hooks/filter-admin-settings-users";
import { UserTable } from "../ats/components/UserTable";
import { type CreateUserFormValues, CreateUserModal } from "../ats/CreateUserModal";
import { type EditUserFormValues, EditUserModal } from "../ats/EditUserModal";

// Align with Figma: unselected product tags are outlined (black border, no fill); the selected
// tag's border turns blue with no fill either; disabled tags grey out both text and border.
const useProductTagStyles = createStyles(({ css, token }) => ({
  tag: css`
    background: transparent;
    border: 1px solid ${token.colorText};
    color: ${token.colorText};

    &.ant-tag-checkable-checked {
      background: transparent;
      border-color: ${token.colorPrimary};
      color: ${token.colorPrimary};
    }
  `,
  tagDisabled: css`
    background: transparent;
    border-color: ${token.colorTextDisabled};
    color: ${token.colorTextDisabled};
  `,
}));

// Products with no data source yet: rendered as disabled
const comingSoonProducts = [
  { key: "hr", labelKey: "platform.adminSettings.usersTabTitle_hr" },
  { key: "payroll", labelKey: "platform.adminSettings.usersTabTitle_payroll" },
] as const;

type UsersSettingsProps = {
  availableProductPaths: Set<string>;
};

// eslint-disable-next-line import/no-default-export
export default function UsersSettings(props: UsersSettingsProps) {
  const { availableProductPaths } = props;
  const { t } = useTranslation();
  const { styles, cx } = useProductTagStyles();
  const [searchText, setSearchText] = useState("");
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [createUserForm] = Form.useForm<CreateUserFormValues>();
  const [editUserForm] = Form.useForm<EditUserFormValues>();
  const { message, notification } = App.useApp();
  const {
    mutate: createPlatformUser,
    error: createPlatformUserError,
    isPending: isCreatePlatformUserPending,
    reset: resetCreatePlatformUser,
  } = useCreatePlatformUser();
  const {
    mutate: updateUser,
    error: updateUserError,
    isPending: isUpdateUserPending,
    reset: resetUpdateUser,
  } = useUpdateUserPermission("ats");
  const {
    mutateAsync: sendWelcomeEmail,
    isPending: isSendingWelcomeEmail,
    variables: sendingWelcomeEmailUserId,
  } = useSendWelcomeEmail();
  // Its own instance, so a row's Send again and the new user's send never share one pending state or callbacks
  const { mutate: sendNewUserWelcomeEmail, isPending: isSendingNewUserWelcomeEmail } =
    useSendWelcomeEmail();
  // Failed "Send again" attempts per user while this page is open; the backend only keeps the latest status. A ref, not
  // state: nothing renders it
  const failedRetryCounts = useRef<Record<string, number>>({});
  const { data: users = [], isLoading } = useFetchAtsAdminSettingsUsers();
  const { data: securityGroups } = useFetchSecurityGroups("ats");
  const { hasPermission: hasAtsPermission } = useFetchPermissions("ats");
  const canManageAtsUsers = hasAtsPermission("edit:user.permission");
  const canCreateAtsUsers = hasAtsPermission("create:user");
  const isAtsUserCreationEnabled = useBooleanFlagValue("ats_user_creation", false);
  const isUsersEmailEnabled = useBooleanFlagValue("ats_users_email", false);
  const visibleComingSoonProducts = comingSoonProducts.filter((product) =>
    availableProductPaths.has(product.key)
  );

  const filteredUsers = filterAtsAdminSettingsUsers({
    users,
    searchText,
    selectedSecurityGroups: null,
  });

  const handleClosingCreateUserDialog = () => {
    resetCreatePlatformUser();
    setIsCreateUserDialogOpen(false);
  };

  const getEmailFailedNotificationKey = (userId: string) =>
    `ats-admin-settings-users-email-failed-${userId}`;

  const handleCreatingUser = (values: CreateUserFormValues) =>
    createPlatformUser(
      {
        email: values.email ?? "",
        displayName: values.displayName ?? "",
        securityGroups: values.securityGroupIds ?? [],
      },
      {
        // The send is chained in these callbacks, not in a useEffect: it answers the admin's submit, and our hooks guide
        // keeps useEffect for syncing with external systems. The dialog stays open until the send has finished too.
        onSuccess: (platformUser) => {
          if (!values.emailUser) {
            handleClosingCreateUserDialog();
            message.success(t("platform.adminSettings.users_actionMessage_addSuccess"));

            return;
          }

          sendNewUserWelcomeEmail(platformUser.id, {
            onSuccess: () => {
              handleClosingCreateUserDialog();
              message.success(t("platform.adminSettings.users_actionMessage_addSuccess"));
            },
            onError: () => {
              // The user exists either way; the row also shows the failed status and keeps Send again after the toast
              // closes. The default ~4.5 seconds closed it before an admin could reach Send again, so it stays for 10.
              handleClosingCreateUserDialog();
              notification.error({
                key: getEmailFailedNotificationKey(platformUser.id),
                duration: 10,
                placement: "bottomRight",
                message: (
                  <Typography.Text testIdPrefix="ats-admin-settings-users-email-failed-message">
                    {t("platform.adminSettings.users_emailStatus_addedButFailed", {
                      name: platformUser.displayName,
                    })}
                  </Typography.Text>
                ),
                actions: canManageAtsUsers ? (
                  <Button
                    size="small"
                    testIdPrefix="ats-admin-settings-users-email-failed-send-again"
                    type="link"
                    onClick={() =>
                      handleRetryingWelcomeEmail({
                        userId: platformUser.id,
                        name: platformUser.displayName,
                      })
                    }
                  >
                    {t("platform.adminSettings.users_emailStatus_retry")}
                  </Button>
                ) : undefined,
              });
            },
          });
        },
        onError: (error) => {
          if (error.response?.data.validationErrors) {
            return;
          }

          message.error(t("platform.adminSettings.users_actionMessage_addFailure"));
        },
      }
    );

  // Awaits its own call instead of passing callbacks to mutate: mutate keeps only the latest call's callbacks, so the
  // toast's Send again, which can't be disabled while a row is sending, would drop that row's result
  const handleRetryingWelcomeEmail = async (
    user: Pick<AtsAdminSettingsUser, "userId" | "name">
  ) => {
    // Once a retry starts from the toast or the row, the toast's Send again would only send a second email
    notification.destroy(getEmailFailedNotificationKey(user.userId));

    try {
      await sendWelcomeEmail(user.userId);
    } catch {
      const failedRetries = (failedRetryCounts.current[user.userId] ?? 0) + 1;

      failedRetryCounts.current[user.userId] = failedRetries;
      // The first send already failed before any retry, so a second failed retry is the third attempt
      message.error(
        failedRetries >= 2
          ? t("platform.adminSettings.users_emailStatus_failedAgain", { name: user.name })
          : t("platform.adminSettings.users_emailStatus_failed")
      );

      return;
    }

    failedRetryCounts.current[user.userId] = 0;
    message.success(t("platform.adminSettings.users_emailStatus_sent"));
  };

  const handleClosingEditUserDialog = () => {
    resetUpdateUser();
    setIsEditUserDialogOpen(false);
  };

  const handleEditingUser = (user: AtsAdminSettingsUser) => {
    editUserForm.setFieldValue("userId", user.userId);
    editUserForm.setFieldValue("displayName", user.name);
    editUserForm.setFieldValue("email", user.email);
    editUserForm.setFieldValue(
      "securityGroups",
      user.securityGroups.map((group: SecurityGroup) => group.id)
    );
    setIsEditUserDialogOpen(true);
  };

  const handleUpdatingUser = (values: EditUserFormValues) =>
    updateUser(
      {
        userId: values.userId,
        email: values.email?.trim() ?? "",
        displayName: values.displayName?.trim(),
        securityGroups: values.securityGroups ?? [],
      },
      {
        onSuccess: () => {
          message.success(t("platform.adminSettings.users_actionMessage_updateSuccess"));
          handleClosingEditUserDialog();
        },
        onError: () => {
          message.error(t("platform.adminSettings.users_actionMessage_updateFailure"));
        },
      }
    );

  return (
    <>
      <Flex vertical gap="middle">
        <Typography.Text testIdPrefix="admin-settings-users-heading" strong>
          {t("platform.adminSettings.usersInThePlatform")}
        </Typography.Text>
        <Flex justify="space-between" align="center">
          <Flex gap="small">
            <CheckableTag
              checked
              className={styles.tag}
              data-testid="admin-settings-users-product-ats"
            >
              {t("platform.adminSettings.usersTabTitle_ats")}
            </CheckableTag>
            {/* trigger includes "focus" (matching EmailColumn.tsx's tooltip pattern) and the tag
              gets tabIndex + aria-label so keyboard and screen-reader users can discover the
              "coming soon" state -- CheckableTag isn't focusable or labeled by default. */}
            {visibleComingSoonProducts.map((product) => (
              <Tooltip
                key={product.key}
                title={t("platform.adminSettings.usersTab_comingSoon")}
                trigger={["hover", "focus"]}
              >
                <CheckableTag
                  checked={false}
                  aria-disabled="true"
                  aria-label={t("platform.adminSettings.usersTab_productComingSoon", {
                    product: t(product.labelKey),
                  })}
                  tabIndex={0}
                  className={cx(styles.tag, styles.tagDisabled)}
                  style={{ cursor: "not-allowed" }}
                  data-testid={`admin-settings-users-product-${product.key}`}
                >
                  {t(product.labelKey)}
                </CheckableTag>
              </Tooltip>
            ))}
          </Flex>
          {canCreateAtsUsers && isAtsUserCreationEnabled && (
            <Button
              type="primary"
              testIdPrefix="ats-admin-settings-add-user"
              onClick={() => setIsCreateUserDialogOpen(true)}
            >
              {t("platform.adminSettings.users_button_addUser")}
            </Button>
          )}
        </Flex>
        {/* Intentionally Input.Search to match the Figma design, which differs from Roles & Permissions' design in ats/UserFilters. */}
        <Input.Search
          style={{ width: "400px" }}
          placeholder={t("platform.adminSettings.searchUsers")}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setSearchText("")}
          testIdPrefix="admin-settings-users-search-box"
        />
        <UserTable
          t={t}
          testIdPrefix="ats-admin-settings-users-list"
          users={filteredUsers}
          isLoading={isLoading}
          onEditUser={canManageAtsUsers ? handleEditingUser : undefined}
          isEditUserPending={(user) => user.atsUserProvisioningPending === true}
          emailTooltipTitleKey="platform.adminSettings.users_columns_email_accessTooltip_title"
          emailTooltipContentKey="platform.adminSettings.users_columns_email_accessTooltip_content"
          securityGroupTitleKey="platform.adminSettings.users_columns_securityGroup_ats"
          emailBeforeSecurityGroup
          showEmailStatus={isUsersEmailEnabled}
          onRetryEmail={canManageAtsUsers ? handleRetryingWelcomeEmail : undefined}
          sendingEmailUserId={isSendingWelcomeEmail ? sendingWelcomeEmailUserId : undefined}
        />
      </Flex>
      <CreateUserModal
        open={isCreateUserDialogOpen}
        form={createUserForm}
        securityGroupOptions={securityGroups}
        onCancel={handleClosingCreateUserDialog}
        onSubmit={handleCreatingUser}
        serverError={createPlatformUserError}
        isSubmitting={isCreatePlatformUserPending || isSendingNewUserWelcomeEmail}
        showEmailUser={isUsersEmailEnabled}
      />
      <EditUserModal
        open={isEditUserDialogOpen}
        form={editUserForm}
        securityGroupOptions={securityGroups}
        onCancel={handleClosingEditUserDialog}
        onSubmit={handleUpdatingUser}
        serverError={updateUserError}
        isSubmitting={isUpdateUserPending}
      />
    </>
  );
}
